import { ESectionInfoType, EVariableType } from '../data/Constants';

import {
  getMultiNumericCode,
  getNameWithCode,
  isVariableVisible,
} from './ConfigurationDataHelperFunctions';

import type {
  IConfigurationVariable,
  ISection,
  ISearchOption,
  ISearchDetails,
  IFeatureOrFamily,
  IConfigurationValue,
} from '../../types';
import { AppStore } from '../store/AppStore';

/**
 * Traverses through all sections to find search value matches in variables.
 * @param sections - Array of sections to search through
 * @param searchKey - The search term to match against
 * @param showCode - Whether to include code/ID matching in the search
 * @param highlightResults - Whether to set isSearchValueMatched flags on matched items
 * @returns Search details object containing count and array of matches
 */
export function traverseSearchValueOnSections(
  sections: ISection[],
  searchKey: string,
  highlightResults: boolean
) {
  const showCode = AppStore.getState().userSettings.showCode;
  const searchDetails = { count: 0, matches: [] };
  sections.forEach((section: ISection) => {
    section.isSearchValueMatched = false;
    findSearchValueInVariables(section, searchDetails, searchKey, showCode, highlightResults);

    if (section.sections.length > 0) {
      section.sections.forEach((innerSection) => {
        if (innerSection.type === ESectionInfoType.MultiNumeric) {
          innerSection.isSearchValueMatched = false;
          findSearchValueInMultinumericSection(
            innerSection,
            section,
            searchKey,
            highlightResults,
            searchDetails,
            showCode
          );
        }
        findSearchValueInVariables(
          innerSection,
          searchDetails,
          searchKey,
          showCode,
          highlightResults,
          section
        );
      });
    }
  });
  return searchDetails;
}

/**
 * Finds search value matches in variables of a section.
 * Searches both by name/text and by ID/code if showCode is enabled.
 * For multiselect/singleselect variables, recursively searches their values.
 * @param section - The section containing variables to search
 * @param searchDetails - Accumulator object for search results
 * @param searchKey - The search term to match against
 * @param showCode - Whether to include code/ID matching in the search
 * @param highlightResults - Whether to set isSearchValueMatched flags on matched items
 */
function findSearchValueInVariables(
  section: ISection,
  searchDetails: ISearchDetails,
  searchKey: string,
  showCode: boolean,
  highlightResults: boolean,
  parentSection?: ISection
) {
  const searchValue = searchKey?.trim().toLowerCase() ?? '';
  section.variables.forEach((variable) => {
    if (searchValue) {
      processSearchItem(variable, searchValue, {
        section,
        searchDetails,
        showCode,
        highlightResults,
        idKey: 'id',
        familyName: '',
        parentSection,
      });

      if (
        variable.variableType === EVariableType.Multiselect ||
        variable.variableType === EVariableType.Singleselect
      ) {
        const searchOn = variable.shortSalesText ? 'shortSalesText' : 'name';
        findSearchValueInValues(
          section,
          variable,
          searchDetails,
          searchKey,
          showCode,
          highlightResults,
          searchOn
        );
      }
    }
  });
}

/**
 * Finds search value matches in the values (features) of a configuration variable (family).
 * Searches both by name/text and by value code if showCode is enabled.
 * @param section - The section containing the parent variable
 * @param variable - The parent variable whose values are being searched
 * @param searchDetails - Accumulator object for search results
 * @param searchKey - The search term to match against
 * @param showCode - Whether to include code/ID matching in the search
 * @param highlightResults - Whether to set isSearchValueMatched flags on matched items
 * @param searchOnOfFamily - The field name ('name' or 'shortSalesText') used for the family
 */
function findSearchValueInValues(
  section: ISection,
  variable: IConfigurationVariable,
  searchDetails: ISearchDetails,
  searchKey: string,
  showCode: boolean,
  highlightResults: boolean,
  searchOnOfFamily: string
) {
  const searchValue = searchKey?.trim().toLowerCase() ?? '';
  const familyName = variable[searchOnOfFamily as keyof IConfigurationVariable] as string;

  variable.values.forEach((val: IConfigurationValue) => {
    processSearchItem(val, searchValue, {
      section,
      searchDetails,
      showCode,
      highlightResults,
      idKey: 'value',
      familyName,
    });
  });
}

/**
 * Processes a single item (variable or value) to check if it matches the search criteria.
 * If a match is found, adds it to search results and sets appropriate flags.
 * @param item - The variable or value to process
 * @param searchValue - The lowercase search term to match against
 * @param options - Configuration options for the search processing
 * @param options.section - The section containing the item
 * @param options.searchDetails - Accumulator object for search results
 * @param options.showCode - Whether to include code/ID matching in the search
 * @param options.highlightResults - Whether to set isSearchValueMatched flags on matched items
 * @param options.idKey - The field name for ID matching ('id' for variables, 'value' for variable.values)
 * @param options.familyName - The family name to include in search results
 * @param options.parentSection - Optional parent section to mark as matched
 */
function processSearchItem(
  item: IConfigurationVariable | IConfigurationValue,
  searchValue: string,
  options: {
    section: ISection;
    searchDetails: ISearchDetails;
    showCode: boolean;
    highlightResults: boolean;
    idKey: 'id' | 'value';
    familyName: string;
    parentSection?: ISection;
  }
) {
  const { section, searchDetails, showCode, highlightResults, idKey, familyName, parentSection } =
    options;

  item.isSearchValueMatched = false;
  const searchOn = item.shortSalesText ? 'shortSalesText' : 'name';

  if (isVariableVisible(item)) {
    const isNameMatch = checkSearchMatchesWithName(item, searchValue, searchOn);
    if (isNameMatch) {
      setSearchMatches(
        item,
        section,
        highlightResults,
        searchDetails,
        {
          title: item[searchOn] as string,
          familyName,
          displayName: undefined,
        },
        parentSection
      );
    } else if (showCode && checkSearchMatchesWithId(item, searchValue, idKey)) {
      const idValue = item[idKey] as string;
      setSearchMatches(
        item,
        section,
        highlightResults,
        searchDetails,
        {
          title: idValue + '-' + item[searchOn],
          familyName,
          displayName: getNameWithCode(item as IFeatureOrFamily),
        },
        parentSection
      );
    }
  }
}

/**
 * Checks if a variable's name or text matches the search value.
 * @param variable - The variable or value to check
 * @param searchValue - The lowercase search term to match against
 * @param searchOn - The field name to search in ('name' or 'shortSalesText')
 * @returns True if the variable's field contains the search value (case-insensitive)
 */
function checkSearchMatchesWithName(
  variable: IConfigurationVariable | IConfigurationValue | ISection,
  searchValue: string,
  searchOn: string
): boolean {
  return (variable[searchOn as keyof typeof variable] as string)
    ?.toLowerCase()
    .includes(searchValue);
}

/**
 * Checks if a variable's ID or value code matches the search value.
 * If the search value contains a hyphen, only the part before the hyphen is used for matching.
 * @param variable - The variable or value to check
 * @param searchValue - The lowercase search term to match against
 * @param idKey - The field name to search in ('id' for variables, 'value' for configuration values)
 * @returns True if the variable's ID/value contains the search term (case-insensitive)
 */
function checkSearchMatchesWithId(
  variable: IConfigurationVariable | IConfigurationValue | ISection,
  searchValue: string,
  idKey: string
): boolean {
  return (variable[idKey as keyof typeof variable] as string)
    .toLowerCase()
    .includes(
      searchValue.includes('-') ? searchValue.substring(0, searchValue.indexOf('-')) : searchValue
    );
}

/**
 * Adds a search match to the results if not already present.
 * @param searchDetails - Accumulator object to add the match to
 * @param searchOption - The search option object to add
 */
function addSearchMatch(searchDetails: ISearchDetails, searchOption: ISearchOption) {
  const index = searchDetails.matches?.findIndex(
    (s: ISearchOption) => s.title === searchOption.title
  );
  if (searchDetails && index === -1) {
    searchDetails.matches?.push(searchOption);
    searchDetails.count = searchDetails.count + 1;
  }
}

/**
 * Adds a matched variable/value to the search results and sets search flags.
 * Validates that guarded variables only appear in Guarded sections and vice versa.
 * @param variable - The variable or value that matched the search
 * @param section - The section containing the variable
 * @param highlightResults - Whether to set isSearchValueMatched flags on matched items
 * @param searchDetails - Accumulator object to add the match to
 * @param searchMatchKey - The search option object describing the match
 */
function setSearchMatches(
  variable: IConfigurationVariable | IConfigurationValue,
  section: ISection,
  highlightResults: boolean,
  searchDetails: ISearchDetails,
  searchMatchKey: ISearchOption,
  parentSection?: ISection
) {
  //Validating the variables belongs to Guarded section and other sections
  const isValidVariable =
    section?.id === ESectionInfoType.Guarded || !('isGuarded' in variable && variable.isGuarded);
  if (section && highlightResults && isValidVariable) {
    variable.isSearchValueMatched = true;

    if (parentSection) {
      parentSection.isSearchValueMatched = true; //in case of multinumeric set isSearchValueMatched to parent section
    } else {
      section.isSearchValueMatched = true;
    }
  }
  //Pushing variables by segregating guarded and non guarded sections
  if (isValidVariable) {
    addSearchMatch(searchDetails, searchMatchKey);
  }
}

/**
 * Adds a matched multi-numeric section to the search results and sets search flags.
 * Records the match with either the section name or code-prefixed name depending on search type.
 * @param parentSection - The parent section to mark as matched
 * @param section - The multi-numeric section that matched the search
 * @param highlightResults - Whether to set isSearchValueMatched flags on matched items
 * @param searchDetails - Accumulator object to add the match to
 * @param title - The title for the search match
 * @param displayName - Optional display name for the search match
 */
function setSearchMatchesForMultinumeric(
  parentSection: ISection,
  section: ISection,
  highlightResults: boolean,
  searchDetails: ISearchDetails,
  title: string,
  displayName?: string
) {
  if (highlightResults) {
    section.isSearchValueMatched = true;
    parentSection.isSearchValueMatched = true;
  }
  addSearchMatch(searchDetails, {
    title,
    familyName: '',
    displayName,
  });
}

/**
 * Finds search value matches in a multi-numeric section.
 * Searches both by section name and by section ID/code if showCode is enabled.
 * If a match is found, marks both the section and parent section and adds to search results.
 * @param section - The multi-numeric section to search
 * @param parentSection - The parent section containing the multi-numeric section
 * @param searchKey - The search term to match against
 * @param highlightResults - Whether to set isSearchValueMatched flags on matched items
 * @param searchDetails - Accumulator object for search results
 * @param showCode - Whether to include code/ID matching in the search
 */
function findSearchValueInMultinumericSection(
  section: ISection,
  parentSection: ISection,
  searchKey: string,
  highlightResults: boolean,
  searchDetails: ISearchDetails,
  showCode: boolean
) {
  // Skip sections with no selectable variables
  if (section.isItemsAvailable) return;

  const isNameMatch = checkSearchMatchesWithName(
    section,
    searchKey?.trim().toLowerCase() ?? '',
    'name'
  );
  if (isNameMatch) {
    setSearchMatchesForMultinumeric(
      parentSection,
      section,
      highlightResults,
      searchDetails,
      section.name ?? ''
    );
  } else if (
    showCode &&
    checkSearchMatchesWithId(section, searchKey?.trim().toLowerCase() ?? '', 'id')
  ) {
    const secionId = getMultiNumericCode(section.id);
    setSearchMatchesForMultinumeric(
      parentSection,
      section,
      highlightResults,
      searchDetails,
      secionId + '-' + section.name,
      `${section.name} (${secionId})`
    );
  }
}
